/**
 * DASHBOARD-RESULTS-AXES-ANIMATED.WIDGET
 * Spider/radar des axes — remplace le rendu Plotly (dashboard-results-axes.widget.tsx,
 * laissé intact en référence/fallback) par du SVG custom, même esprit que la rosace
 * animée : le polygone "grossit" depuis le centre au chargement (le rayon de chaque
 * sommet suit useCountUpList, donc l'entrée en éventail est gratuite — pas besoin
 * d'animation séparée). Comportement de clic identique à l'original :
 * props.onClick(axisId) sur un marqueur pour filtrer par axe.
 *
 * Chaque label est positionné à mi-chemin entre SON dot (sommet du polygone) et le
 * cercle extérieur — à cheval entre les deux — plutôt qu'à un rayon unique fixe pour
 * tous les axes. Le calcul utilise la note CIBLE (axis.note), pas la valeur animée
 * (animatedNotes), pour que le label reste immobile pendant que le polygone grossit ;
 * sinon il glisserait pendant toute l'animation d'entrée. MAX_RADIUS garde une marge
 * généreuse par rapport au canevas pour qu'un label proche du cercle (score élevé) ne
 * déborde jamais, quelle que soit la longueur du libellé.
 */

import { withTranslation, WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import { motion } from "framer-motion"
import { Session } from "@/redux/_session.types"
import { Axis } from "@/redux/axis.types"
import { useCountUpList } from "@/utils/use-count-up.utils"

interface StateProps extends WithTranslation {
  _session: Session
}

interface OwnProps {
  currentAxes: Axis[]
  onClick?: Function
}

type Props = StateProps & OwnProps

const SIZE = 420
const CENTER = SIZE / 2
//Marge élargie (vs simple trait du cercle) pour laisser la place aux labels qui peuvent
//s'approcher du cercle quand le score de l'axe est élevé
const MAX_RADIUS = CENTER - 55
const NOTE_MAX = 100 //Échelle brute d'Axis.note (0-100), indépendante du mode d'affichage
const LABEL_LINE_HEIGHT = 15

//Wrap sur 2 lignes max (pas de troncature) — la 2e ligne récupère le reste si le libellé est trop long
function wrapLabel(text: string, maxCharsPerLine: number = 13): string[] {
  const words = text.split(" ")
  const lines: string[] = []
  let current = ""

  words.forEach((word) => {
    const candidate = current ? current + " " + word : word
    if (!current || candidate.length <= maxCharsPerLine) {
      current = candidate
    } else {
      lines.push(current)
      current = word
    }
  })
  if (current) lines.push(current)

  if (lines.length > 2) {
    return [lines[0], lines.slice(1).join(" ")]
  }
  return lines
}

function polarPoint(radius: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180
  return { x: CENTER + radius * Math.cos(rad), y: CENTER + radius * Math.sin(rad) }
}

function DashboardResultsAxesAnimatedWidget(props: Props) {
  const { t } = props

  const filteredAxes = props.currentAxes.filter((x: Axis) => x.note)
  const animatedNotes = useCountUpList(filteredAxes.map((x) => x.note))
  const isSatisfaction = props._session.dashboardDisplaySettings.dashboardDisplayMode !== "note"

  function click(axisId: string) {
    if (props.onClick) {
      props.onClick(axisId)
    }
  }

  function formatScore(note: number): string {
    return isSatisfaction ? note.toFixed(0) + "%" : (note / 10).toFixed(1) + "/10"
  }

  if (filteredAxes.length === 0) {
    return <div style={{ height: SIZE, width: SIZE, maxWidth: "100%" }} />
  }

  const step = 360 / filteredAxes.length

  const points = filteredAxes.map((axis, i) => {
    const angle = i * step
    const radius = (animatedNotes[i] / NOTE_MAX) * MAX_RADIUS
    return { ...polarPoint(radius, angle), axis, angle }
  })

  const polygonPoints = points.map((p) => `${p.x},${p.y}`).join(" ")

  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      style={{ width: "100%", height: "auto", maxWidth: SIZE, display: "block" }}
      aria-label={t("axes")}>
      <circle cx={CENTER} cy={CENTER} r={MAX_RADIUS} fill="none" stroke="#e5e5e5" strokeWidth={1} />

      {filteredAxes.map((axis, i) => {
        const end = polarPoint(MAX_RADIUS, i * step)
        return <line key={axis.id} x1={CENTER} y1={CENTER} x2={end.x} y2={end.y} stroke="#e5e5e5" strokeWidth={1} />
      })}

      <polygon
        points={polygonPoints}
        fill={props._session.accountColors.brandPrimary}
        fillOpacity={0.35}
        stroke="#111C2B"
        strokeWidth={1.5}
      />

      {points.map((p) => (
        <motion.circle
          key={p.axis.id}
          cx={p.x}
          cy={p.y}
          r={7}
          fill={p.axis.color}
          stroke="white"
          strokeWidth={1.5}
          style={{ cursor: "pointer" }}
          whileHover={{ scale: 1.3 }}
          onClick={() => click(p.axis.id)}
        />
      ))}

      {filteredAxes.map((axis, i) => {
        const angle = i * step
        //Rayon cible (pas animé) du dot de cet axe, pour que le label reste immobile
        //pendant l'animation d'entrée du polygone
        const targetRadius = (axis.note / NOTE_MAX) * MAX_RADIUS
        const labelPoint = polarPoint((targetRadius + MAX_RADIUS) / 2, angle)
        const lines = wrapLabel(axis.label)
        const totalLines = lines.length + 1 //+1 pour la ligne de score
        const firstDy = -((totalLines - 1) * LABEL_LINE_HEIGHT) / 2
        return (
          <text
            key={axis.id}
            x={labelPoint.x}
            y={labelPoint.y}
            textAnchor="middle"
            fontSize={13}
            fontFamily="Open Sans"
            fill="#111C2B"
            stroke="white"
            strokeWidth={3}
            paintOrder="stroke"
            style={{ cursor: "pointer" }}
            onClick={() => click(axis.id)}>
            {lines.map((line, li) => (
              <tspan key={li} x={labelPoint.x} dy={li === 0 ? firstDy : LABEL_LINE_HEIGHT}>
                {line}
              </tspan>
            ))}
            <tspan x={labelPoint.x} dy={LABEL_LINE_HEIGHT} fontWeight="bold">
              {formatScore(animatedNotes[i])}
            </tspan>
          </text>
        )
      })}
    </svg>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session
})

export default connect(mapStateToProps)(withTranslation()(DashboardResultsAxesAnimatedWidget))
